// The pause menu, assembled.
//
// **Rendered unconditionally, with `screen={null}` for closed.** The menu is
// `keepMounted` and its diagnostics footer is what `e2e/live-smoke.js` reads
// `move-count` and `state-hash` off with nothing open — a gate out here would
// unmount the very thing the smoke does not open a door to reach.
//
// **The three reads below that are not the contract's** (PLAN-CLIENT-SPLIT
// day 8): the powers list wants a `SessionView.powers`, the costs page a
// `game.board.costs()`, and the feedback form and the hash want the log and
// `Status.hash`. They are gathered here, in one zone, so `PauseMenu` and its
// screens are typed on what they draw and nothing else.

import { useGameState } from "src/client/ai/hooks/selectors";
import { useGameSnapshot, useStore } from "src/client/ai/hooks/useGameStore";
import { useNet } from "src/client/ai/hooks/useNet";
import { hasPower, POWER_KIND, POWER_LIST } from "src/engine/rules/campaign/powers";
import { hashToHex } from "src/engine/rules/hash";
import { STRUCTURE_NAMES } from "src/engine/rules/industry/structures";
import type { CameraPrefs } from "../cameraPrefs";
import { PauseMenu, type PauseScreen } from "../PauseMenu";
import type { PowerRow } from "../pause/PowersScreen";
import { useBoardHandle } from "./BoardHandle";
import { useShellMode } from "./ShellMode";

export function PauseMenuZone({
  screen,
  onScreen,
  side,
  camera,
  onCamera,
  cameraFixed
}: {
  /** Which screen is up, or null for closed. */
  screen: PauseScreen | null;
  onScreen: (screen: PauseScreen | null) => void;
  /**
   * Whose economy the chrome is about — the shell's single definition. The
   * rules' number: the one reader is `hasPower`, which is the rules'.
   */
  side: number;
  camera: CameraPrefs;
  onCamera: (patch: Partial<CameraPrefs>) => void;
  /** A standard board never gets a camera offset (`useCameraLooks`). */
  cameraFixed: boolean;
}) {
  const board = useBoardHandle();
  const { net } = useNet();
  const store = useStore();
  const { hash } = useGameSnapshot();
  const state = useGameState();
  const { scenario, watching, onExit, onRestart, onPrivacy } = useShellMode();

  // A reading of the board, every render: `hasPower` asks the standing
  // structures and nothing else, so a captured structure drops off the list
  // by construction.
  const powers: readonly PowerRow[] | null = state.run
    ? POWER_LIST.map((power) => ({
        id: power.id,
        glyph: power.glyph,
        name: power.name,
        structure: STRUCTURE_NAMES[POWER_KIND[power.id]],
        on: hasPower(state.industry, side, power.id)
      }))
    : null;

  return (
    /* **The pause menu**: the old View menu's settings, the rules, the
       costs, what powers are on, feedback, restart and the way out, with the
       diagnostics as its footer (PLAN-UI §28.3). One surface, so one thing
       is open at a time — the header's four buttons could each be open
       beside the others.

       **The feedback state is captured when the dialog opens**, not when
       Send is pressed: a report is about the board somebody was looking at
       when something went wrong, and a run that has moved on three turns is
       a different board. */
    <PauseMenu
      screen={screen}
      onScreen={onScreen}
      inRun={Boolean(state.run)}
      powers={powers}
      watching={watching}
      camera={camera}
      onCamera={onCamera}
      cameraFixed={cameraFixed}
      onResetView={() => board.current?.resetView()}
      feedback={{
        state,
        mode: scenario ? "scenario" : state.run ? "run" : net.room ? "room" : "local",
        room: net.room ?? null,
        log: store.getSnapshot().log,
        onPrivacy
      }}
      restartLabel={state.run ? "Restart level" : scenario ? "New run" : "New game"}
      onRestart={onRestart}
      onExit={onExit}
      plies={state.history.length}
      hash={hashToHex(hash)}
      build={__BUILD_ID__}
    />
  );
}
